import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { loginRateLimiter } from '../utils/rateLimiter';
import '../styles/Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const { login, signInWithGoogle, resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const rateLimitCheck = loginRateLimiter.isRateLimited(email);
    if (rateLimitCheck.limited) {
      setError(`Too many login attempts. Please try again in ${rateLimitCheck.timeRemaining} seconds.`);
      setLoading(false);
      return;
    }

    try {
      await login(email, password);
      navigate('/', { replace: true });
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithGoogle();
      navigate('/', { replace: true });
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await resetPassword(email);
      setError('Password reset link sent! Check your email inbox.');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>Welcome to PingIT</h1>
        <p className="subtitle">Sign in to your account</p>
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleEmailSignIn}>
          <div className="input-group">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
            <i className="fas fa-envelope"></i>
          </div>
          
          <div className="input-group">
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
            <i className="fas fa-lock"></i>
          </div>
          
          <button 
            type="submit" 
            className="sign-in-button"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign in with Email'}
          </button>
        </form>
        
        <div className="forgot-password">
          <button
            onClick={() => setShowResetPassword(!showResetPassword)}
            className="text-button"
          >
            Forgot Password?
          </button>
        </div>

        {showResetPassword && (
          <div className="reset-password-section">
            <p>Enter your email to receive a password reset link.</p>
            <button
              onClick={handleResetPassword}
              className="reset-button"
              disabled={loading}
            >
              Send Reset Link
            </button>
          </div>
        )}
        
        <div className="divider">
          <span>OR CONTINUE WITH</span>
        </div>
        
        <button 
          onClick={handleGoogleSignIn} 
          className="google-sign-in"
          disabled={loading}
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" />
          Sign in with Google
        </button>
        
        <p className="signup-link">
          Don't have an account? <a href="/signup">Sign up</a>
        </p>
      </div>
    </div>
  );
};

export default Login;
